import { useState, useEffect, useCallback } from 'react';
import {
  Mail,
  CheckCircle,
  XCircle,
  Clock,
  ChevronRight,
  AlertTriangle,
  TrendingUp,
  RefreshCw,
  Shield,
  ArrowRight,
} from 'lucide-react';
import { api } from '../lib/api.js';

// ─── Local Types ─────────────────────────────────────────────

type JourneyStage =
  | 'discovery'
  | 'authorize_senders'
  | 'ready_for_quarantine'
  | 'quarantine'
  | 'monitor_quarantine'
  | 'ready_for_reject'
  | 'reject'
  | 'complete';

interface DmarcJourney {
  id: string;
  domain: string;
  domain_id: string;
  current_stage: JourneyStage;
  started_at: string;
  completed_at?: string;
  total_volume_30d: number;
  authorized_volume_30d: number;
  unauthorized_volume_30d: number;
  authorized_sender_count: number;
  unauthorized_sender_count: number;
  pass_rate: number;
  quarantine_false_positive_rate?: number;
  quarantine_started_at?: string;
  quarantine_days_stable?: number;
  ready_for_quarantine: boolean;
  ready_for_reject: boolean;
  blockers: string[];
  last_promoted_at?: string;
  updated_at: string;
}

interface DmarcSender {
  id: string;
  source_ip: string;
  ip_hostname?: string;
  sender_label?: string;
  total_messages: number;
  passed: number;
  failed: number;
  is_authorized?: boolean;
  is_blocked?: boolean;
  volume_pct: number;
  last_seen_at: string;
}

interface DmarcPromotionLog {
  id: string;
  from_stage: string;
  to_stage: string;
  promoted_by: string;
  promotion_type: 'manual' | 'auto';
  readiness_snapshot: {
    pass_rate: number;
    authorized_volume_pct: number;
    unauthorized_senders: number;
    quarantine_false_positive_rate?: number;
  };
  created_at: string;
}

interface JourneysResponse {
  data: DmarcJourney[];
}

interface JourneyDetailResponse {
  data: DmarcJourney;
  promotions: DmarcPromotionLog[];
}

interface SendersResponse {
  data: DmarcSender[];
  total_volume: number;
}

// ─── Stage Metadata ──────────────────────────────────────────

interface StageInfo {
  label: string;
  description: string;
  color: string;
}

const STAGE_INFO: Record<JourneyStage, StageInfo> = {
  discovery: {
    label: 'Discovery',
    description: 'Identifying all mail sources from DMARC aggregate reports.',
    color: 'blue',
  },
  authorize_senders: {
    label: 'Authorize Senders',
    description: 'Review each sending source and mark as authorized or blocked.',
    color: 'yellow',
  },
  ready_for_quarantine: {
    label: 'Ready for Quarantine',
    description: 'Pass rate ≥90% and all active senders authorized. Ready to enable quarantine.',
    color: 'emerald',
  },
  quarantine: {
    label: 'Quarantine Active',
    description: 'Policy promoted to p=quarantine pct=25. Monitoring for false positives.',
    color: 'orange',
  },
  monitor_quarantine: {
    label: 'Monitoring Quarantine',
    description:
      'Watching quarantine impact. Need <1% false positives for 14 stable days before reject.',
    color: 'yellow',
  },
  ready_for_reject: {
    label: 'Ready for Reject',
    description:
      'Quarantine stable with <1% false positives for 14+ days. Ready to enforce p=reject.',
    color: 'emerald',
  },
  reject: {
    label: 'Reject Enforced',
    description: 'Policy is p=reject. Unauthenticated mail is rejected at receiving MTAs.',
    color: 'emerald',
  },
  complete: {
    label: 'Complete',
    description: 'DMARC enforcement achieved. Domain is fully protected.',
    color: 'emerald',
  },
};

const STAGE_ORDER: JourneyStage[] = [
  'discovery',
  'authorize_senders',
  'ready_for_quarantine',
  'quarantine',
  'monitor_quarantine',
  'ready_for_reject',
  'reject',
  'complete',
];

// ─── Helpers ─────────────────────────────────────────────────

function stageIndex(stage: JourneyStage): number {
  return STAGE_ORDER.indexOf(stage);
}

function readinessScore(journey: DmarcJourney): number {
  const stage = stageIndex(journey.current_stage);
  const total = STAGE_ORDER.length - 1;
  const stageBase = Math.round((stage / total) * 100);

  // Within current stage, estimate sub-progress
  if (journey.current_stage === 'discovery' || journey.current_stage === 'authorize_senders') {
    const passRatePct = Math.round(journey.pass_rate * 100);
    return Math.min(stageBase + Math.round(passRatePct / total), stageBase + 10);
  }
  return stageBase;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ─── Sub-components ──────────────────────────────────────────

function ReadinessCircle({ score }: { score: number }) {
  const r = 34;
  const circumference = 2 * Math.PI * r;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 90 ? '#10b981' : score >= 60 ? '#f59e0b' : '#ef4444';

  return (
    <div className="flex flex-col items-center gap-1">
      <svg width="88" height="88" className="-rotate-90">
        <circle cx="44" cy="44" r={r} stroke="#e5e7eb" strokeWidth="8" fill="none" />
        <circle
          cx="44"
          cy="44"
          r={r}
          stroke={color}
          strokeWidth="8"
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.5s ease' }}
        />
      </svg>
      <span className="text-2xl font-bold text-gray-900 -mt-16">{score}%</span>
      <span className="text-xs text-gray-500 mt-8">Readiness</span>
    </div>
  );
}

function StepperBar({ journey }: { journey: DmarcJourney }) {
  const currentIdx = stageIndex(journey.current_stage);
  return (
    <div className="flex items-center gap-0 overflow-x-auto pb-2">
      {STAGE_ORDER.map((stage, idx) => {
        const isDone = idx < currentIdx;
        const isCurrent = idx === currentIdx;
        const info = STAGE_INFO[stage];
        return (
          <div key={stage} className="flex items-center flex-shrink-0">
            <div className="flex flex-col items-center gap-1">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 ${
                  isDone
                    ? 'bg-emerald-500 border-emerald-500 text-white'
                    : isCurrent
                      ? 'bg-white border-emerald-500 text-emerald-600'
                      : 'bg-white border-gray-300 text-gray-400'
                }`}
              >
                {isDone ? <CheckCircle className="w-4 h-4" /> : idx + 1}
              </div>
              <span
                className={`text-xs font-medium text-center w-16 leading-tight ${
                  isCurrent ? 'text-emerald-700' : isDone ? 'text-gray-600' : 'text-gray-400'
                }`}
              >
                {info.label}
              </span>
            </div>
            {idx < STAGE_ORDER.length - 1 && (
              <div
                className={`h-0.5 w-8 mx-1 mb-5 flex-shrink-0 ${
                  idx < currentIdx ? 'bg-emerald-400' : 'bg-gray-200'
                }`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function SenderRow({
  sender,
  totalVolume,
  onAuthorize,
  onBlock,
  loading,
}: {
  sender: DmarcSender;
  totalVolume: number;
  onAuthorize: (ip: string) => void;
  onBlock: (ip: string) => void;
  loading: boolean;
}) {
  const pct = totalVolume > 0 ? Math.round((sender.total_messages / totalVolume) * 100) : 0;
  const label = sender.sender_label ?? sender.ip_hostname ?? sender.source_ip;
  const statusClass = sender.is_authorized
    ? 'bg-emerald-100 text-emerald-700'
    : sender.is_blocked
      ? 'bg-red-100 text-red-700'
      : 'bg-gray-100 text-gray-600';
  const statusLabel = sender.is_authorized ? 'Authorized' : sender.is_blocked ? 'Blocked' : 'Unknown';

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50">
      <td className="px-4 py-3">
        <div className="font-medium text-gray-900 text-sm">{label}</div>
        <div className="text-xs text-gray-500">{sender.source_ip}</div>
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">{sender.total_messages.toLocaleString()}</td>
      <td className="px-4 py-3 text-sm text-gray-600">{pct}%</td>
      <td className="px-4 py-3">
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusClass}`}>
          {statusLabel}
        </span>
      </td>
      <td className="px-4 py-3 text-xs text-gray-500">
        {new Date(sender.last_seen_at).toLocaleDateString()}
      </td>
      <td className="px-4 py-3">
        <div className="flex gap-2">
          {!sender.is_authorized && (
            <button
              disabled={loading}
              onClick={() => onAuthorize(sender.source_ip)}
              className="px-2 py-1 text-xs bg-emerald-100 text-emerald-700 hover:bg-emerald-200 rounded font-medium disabled:opacity-50"
            >
              Authorize
            </button>
          )}
          {!sender.is_blocked && (
            <button
              disabled={loading}
              onClick={() => onBlock(sender.source_ip)}
              className="px-2 py-1 text-xs bg-red-100 text-red-700 hover:bg-red-200 rounded font-medium disabled:opacity-50"
            >
              Block
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// ─── Main Page ───────────────────────────────────────────────

export function DmarcJourneyPage() {
  const [journeys, setJourneys] = useState<DmarcJourney[]>([]);
  const [selectedJourney, setSelectedJourney] = useState<DmarcJourney | null>(null);
  const [promotions, setPromotions] = useState<DmarcPromotionLog[]>([]);
  const [senders, setSenders] = useState<DmarcSender[]>([]);
  const [totalSenderVolume, setTotalSenderVolume] = useState(0);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingSenders, setLoadingSenders] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showNewJourneyForm, setShowNewJourneyForm] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [newDomainId, setNewDomainId] = useState('');

  // Load journey list
  const loadJourneys = useCallback(async () => {
    setLoadingList(true);
    setError(null);
    try {
      const res = await api<JourneysResponse>('/dmarc/journey');
      setJourneys(res.data ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load journeys');
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    void loadJourneys();
  }, [loadJourneys]);

  // Load journey detail
  const loadDetail = useCallback(async (journeyId: string) => {
    setLoadingDetail(true);
    setActionError(null);
    try {
      const res = await api<JourneyDetailResponse>(`/dmarc/journey/${journeyId}`);
      setSelectedJourney(res.data);
      setPromotions(res.promotions ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load journey detail');
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  // Load senders for a journey
  const loadSenders = useCallback(async (journeyId: string) => {
    setLoadingSenders(true);
    try {
      const res = await api<SendersResponse>(`/dmarc/journey/${journeyId}?action=senders`);
      setSenders(res.data ?? []);
      setTotalSenderVolume(res.total_volume ?? 0);
    } catch {
      setSenders([]);
    } finally {
      setLoadingSenders(false);
    }
  }, []);

  useEffect(() => {
    if (selectedJourney) {
      void loadDetail(selectedJourney.id);
      if (
        selectedJourney.current_stage === 'discovery' ||
        selectedJourney.current_stage === 'authorize_senders'
      ) {
        void loadSenders(selectedJourney.id);
      }
    }
  }, [selectedJourney?.id]);

  // Authorize sender
  const handleAuthorize = async (journeyId: string, senderIp: string) => {
    setActionLoading(true);
    setActionError(null);
    try {
      await api(`/dmarc/journey/${journeyId}?action=authorize_sender`, {
        method: 'POST',
        body: JSON.stringify({ sender_ip: senderIp }),
      });
      await loadDetail(journeyId);
      await loadSenders(journeyId);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to authorize sender');
    } finally {
      setActionLoading(false);
    }
  };

  // Block sender
  const handleBlock = async (journeyId: string, senderIp: string) => {
    setActionLoading(true);
    setActionError(null);
    try {
      await api(`/dmarc/journey/${journeyId}?action=block_sender`, {
        method: 'POST',
        body: JSON.stringify({ sender_ip: senderIp }),
      });
      await loadDetail(journeyId);
      await loadSenders(journeyId);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to block sender');
    } finally {
      setActionLoading(false);
    }
  };

  // Promote stage
  const handlePromote = async (journeyId: string) => {
    setActionLoading(true);
    setActionError(null);
    try {
      await api(`/dmarc/journey/${journeyId}?action=promote`, { method: 'POST' });
      await loadDetail(journeyId);
      await loadJourneys();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Cannot promote yet');
    } finally {
      setActionLoading(false);
    }
  };

  // Refresh metrics
  const handleRefresh = async (journeyId: string) => {
    setActionLoading(true);
    setActionError(null);
    try {
      await api(`/dmarc/journey/${journeyId}?action=refresh`, { method: 'POST' });
      await loadDetail(journeyId);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to refresh metrics');
    } finally {
      setActionLoading(false);
    }
  };

  // Create new journey
  const handleCreateJourney = async () => {
    if (!newDomain.trim()) return;
    setActionLoading(true);
    setActionError(null);
    try {
      const res = await api<{ data: DmarcJourney }>('/dmarc/journey', {
        method: 'POST',
        body: JSON.stringify({
          domain: newDomain.trim(),
          domain_id: newDomainId.trim() || `domain-${Date.now()}`,
        }),
      });
      setShowNewJourneyForm(false);
      setNewDomain('');
      setNewDomainId('');
      await loadJourneys();
      setSelectedJourney(res.data);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to start journey');
    } finally {
      setActionLoading(false);
    }
  };

  // Authorize all unknown senders with known labels
  const handleAuthorizeAllKnown = async (journeyId: string) => {
    const unknown = senders.filter(
      (s) => !s.is_authorized && !s.is_blocked && s.sender_label,
    );
    setActionLoading(true);
    setActionError(null);
    try {
      for (const s of unknown) {
        await api(`/dmarc/journey/${journeyId}?action=authorize_sender`, {
          method: 'POST',
          body: JSON.stringify({ sender_ip: s.source_ip }),
        });
      }
      await loadDetail(journeyId);
      await loadSenders(journeyId);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to bulk authorize');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Journey List view ──

  if (!selectedJourney) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">DMARC Journey Wizard</h2>
            <p className="text-gray-600 mt-1">
              Guide your domains from p=none to full p=reject enforcement with step-by-step
              readiness checks.
            </p>
          </div>
          <button
            onClick={() => setShowNewJourneyForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium"
          >
            <Mail className="w-4 h-4" />
            Start DMARC Journey
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {showNewJourneyForm && (
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Start a New DMARC Journey</h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Domain</label>
                <input
                  type="text"
                  placeholder="example.com"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Domain ID <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  placeholder="From email domains list"
                  value={newDomainId}
                  onChange={(e) => setNewDomainId(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
            </div>
            {actionError && (
              <p className="text-red-600 text-sm mb-3">{actionError}</p>
            )}
            <div className="flex gap-3">
              <button
                disabled={actionLoading || !newDomain.trim()}
                onClick={handleCreateJourney}
                className="px-4 py-2 bg-emerald-600 text-white rounded-md text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
              >
                {actionLoading ? 'Starting…' : 'Start Journey'}
              </button>
              <button
                onClick={() => {
                  setShowNewJourneyForm(false);
                  setActionError(null);
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-200"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {loadingList ? (
          <div className="flex items-center justify-center h-40">
            <div className="text-gray-500">Loading DMARC journeys…</div>
          </div>
        ) : journeys.length === 0 ? (
          <div className="bg-white border border-dashed border-gray-300 rounded-lg p-12 text-center">
            <Shield className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-lg font-medium">No DMARC journeys yet</p>
            <p className="text-gray-400 text-sm mt-1">
              Start a journey for each domain to guide it to full p=reject enforcement.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {journeys.map((journey) => {
              const currentIdx = stageIndex(journey.current_stage);
              const progressPct = Math.round((currentIdx / (STAGE_ORDER.length - 1)) * 100);
              const info = STAGE_INFO[journey.current_stage];
              const colorMap: Record<string, string> = {
                emerald: 'bg-emerald-100 text-emerald-700',
                yellow: 'bg-yellow-100 text-yellow-700',
                blue: 'bg-blue-100 text-blue-700',
                orange: 'bg-orange-100 text-orange-700',
                red: 'bg-red-100 text-red-700',
              };
              const badgeClass = colorMap[info.color] ?? 'bg-gray-100 text-gray-700';

              return (
                <div
                  key={journey.id}
                  onClick={() => setSelectedJourney(journey)}
                  className="bg-white border border-gray-200 rounded-lg p-5 hover:border-emerald-300 hover:shadow-sm cursor-pointer transition-all"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{journey.domain}</h3>
                      <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${badgeClass}`}>
                        {info.label}
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-500">
                        Pass rate:{' '}
                        <span
                          className={`font-semibold ${
                            journey.pass_rate >= 0.9
                              ? 'text-emerald-600'
                              : journey.pass_rate >= 0.7
                                ? 'text-yellow-600'
                                : 'text-red-600'
                          }`}
                        >
                          {Math.round(journey.pass_rate * 100)}%
                        </span>
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        Updated {formatDate(journey.updated_at)}
                      </div>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-emerald-500 h-2 rounded-full transition-all"
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                  <div className="flex justify-between mt-1 text-xs text-gray-400">
                    <span>Discovery</span>
                    <span>{progressPct}% complete</span>
                    <span>Reject</span>
                  </div>
                  {journey.blockers.length > 0 && (
                    <div className="mt-3 flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 rounded px-2 py-1.5">
                      <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>{journey.blockers[0]}</span>
                      {journey.blockers.length > 1 && (
                        <span className="text-amber-500">+{journey.blockers.length - 1} more</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ── Journey Detail view ──

  const journey = selectedJourney;
  const score = readinessScore(journey);
  const info = STAGE_INFO[journey.current_stage];
  const canPromote =
    (journey.current_stage === 'ready_for_quarantine' && journey.ready_for_quarantine) ||
    (journey.current_stage === 'ready_for_reject' && journey.ready_for_reject) ||
    ['discovery', 'authorize_senders', 'quarantine', 'monitor_quarantine', 'reject'].includes(
      journey.current_stage,
    );

  const showSenders =
    journey.current_stage === 'discovery' || journey.current_stage === 'authorize_senders';

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => setSelectedJourney(null)}
          className="text-gray-500 hover:text-gray-900 text-sm underline"
        >
          ← All Journeys
        </button>
        <ChevronRight className="w-4 h-4 text-gray-400" />
        <h2 className="text-xl font-bold text-gray-900">{journey.domain}</h2>
        <button
          disabled={actionLoading}
          onClick={() => handleRefresh(journey.id)}
          className="ml-auto flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${actionLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {loadingDetail ? (
        <div className="flex items-center justify-center h-40">
          <div className="text-gray-500">Loading journey…</div>
        </div>
      ) : (
        <>
          {/* Stepper */}
          <div className="bg-white border border-gray-200 rounded-lg p-5 mb-6">
            <StepperBar journey={journey} />
          </div>

          {/* Current Stage Panel */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-lg font-bold text-gray-900">{info.label}</h3>
                  {journey.current_stage === 'complete' && (
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                  )}
                </div>
                <p className="text-gray-600 text-sm mb-4">{info.description}</p>

                {/* Metrics row */}
                <div className="grid grid-cols-3 gap-4 mb-5">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">30d Volume</p>
                    <p className="text-lg font-bold text-gray-900">
                      {journey.total_volume_30d.toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Pass Rate</p>
                    <p
                      className={`text-lg font-bold ${
                        journey.pass_rate >= 0.9
                          ? 'text-emerald-600'
                          : journey.pass_rate >= 0.7
                            ? 'text-yellow-600'
                            : 'text-red-600'
                      }`}
                    >
                      {Math.round(journey.pass_rate * 100)}%
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Unauthorized Senders</p>
                    <p
                      className={`text-lg font-bold ${
                        journey.unauthorized_sender_count === 0
                          ? 'text-emerald-600'
                          : 'text-red-600'
                      }`}
                    >
                      {journey.unauthorized_sender_count}
                    </p>
                  </div>
                </div>

                {/* Quarantine-specific metrics */}
                {journey.current_stage === 'monitor_quarantine' && (
                  <div className="grid grid-cols-2 gap-4 mb-5">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-xs text-yellow-700">False Positive Rate</p>
                      <p
                        className={`text-lg font-bold ${
                          (journey.quarantine_false_positive_rate ?? 1) < 0.01
                            ? 'text-emerald-600'
                            : 'text-red-600'
                        }`}
                      >
                        {((journey.quarantine_false_positive_rate ?? 0) * 100).toFixed(2)}%
                      </p>
                      <p className="text-xs text-yellow-600">Target: &lt;1%</p>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-xs text-yellow-700">Stable Days</p>
                      <p
                        className={`text-lg font-bold ${
                          (journey.quarantine_days_stable ?? 0) >= 14
                            ? 'text-emerald-600'
                            : 'text-orange-600'
                        }`}
                      >
                        {journey.quarantine_days_stable ?? 0}
                      </p>
                      <p className="text-xs text-yellow-600">Target: 14 days</p>
                    </div>
                  </div>
                )}

                {/* Blockers */}
                {journey.blockers.length > 0 && journey.current_stage !== 'complete' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                      <span className="text-sm font-semibold text-amber-800">
                        Blockers before promotion
                      </span>
                    </div>
                    <ul className="space-y-1">
                      {journey.blockers.map((b, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-sm text-amber-700">
                          <XCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-red-500" />
                          {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {actionError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700">
                    {actionError}
                  </div>
                )}

                {/* Promote button */}
                {journey.current_stage !== 'complete' && (
                  <button
                    disabled={actionLoading || (!canPromote && journey.blockers.length > 0)}
                    onClick={() => handlePromote(journey.id)}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                      canPromote
                        ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    } disabled:opacity-60`}
                  >
                    <TrendingUp className="w-4 h-4" />
                    {actionLoading ? 'Promoting…' : `Promote to ${STAGE_INFO[STAGE_ORDER[stageIndex(journey.current_stage) + 1] ?? 'complete']?.label ?? 'Next Stage'}`}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Readiness circle */}
              <div className="ml-8 flex-shrink-0">
                <ReadinessCircle score={score} />
              </div>
            </div>
          </div>

          {/* Sender Authorization Panel */}
          {showSenders && (
            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-gray-900">Sender Authorization</h3>
                <button
                  disabled={actionLoading || loadingSenders}
                  onClick={() => handleAuthorizeAllKnown(journey.id)}
                  className="px-3 py-1.5 text-sm bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 rounded-md font-medium disabled:opacity-50"
                >
                  Authorize All Known Good
                </button>
              </div>

              {loadingSenders ? (
                <div className="text-center py-8 text-gray-400">Loading senders…</div>
              ) : senders.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Clock className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p>No sender data yet. Waiting for DMARC reports to arrive.</p>
                </div>
              ) : (
                <div className="overflow-hidden rounded-lg border border-gray-200">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-4 py-2.5 font-medium text-gray-600">
                          Sender / IP
                        </th>
                        <th className="text-left px-4 py-2.5 font-medium text-gray-600">
                          Volume (30d)
                        </th>
                        <th className="text-left px-4 py-2.5 font-medium text-gray-600">
                          % of Total
                        </th>
                        <th className="text-left px-4 py-2.5 font-medium text-gray-600">Status</th>
                        <th className="text-left px-4 py-2.5 font-medium text-gray-600">
                          Last Seen
                        </th>
                        <th className="text-left px-4 py-2.5 font-medium text-gray-600">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {senders.map((s) => (
                        <SenderRow
                          key={s.id}
                          sender={s}
                          totalVolume={totalSenderVolume}
                          onAuthorize={(ip) => handleAuthorize(journey.id, ip)}
                          onBlock={(ip) => handleBlock(journey.id, ip)}
                          loading={actionLoading}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Promotion History */}
          {promotions.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Promotion History</h3>
              <div className="relative">
                <div className="absolute left-3.5 top-0 bottom-0 w-0.5 bg-gray-200" />
                <div className="space-y-4">
                  {promotions.map((p) => (
                    <div key={p.id} className="flex gap-4 relative">
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-100 border-2 border-white flex items-center justify-center z-10">
                        <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                      </div>
                      <div className="flex-1 pb-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-gray-900">
                            {STAGE_INFO[p.from_stage as JourneyStage]?.label ?? p.from_stage}
                          </span>
                          <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
                          <span className="text-sm font-medium text-emerald-700">
                            {STAGE_INFO[p.to_stage as JourneyStage]?.label ?? p.to_stage}
                          </span>
                          <span className="text-xs text-gray-400">
                            by {p.promoted_by} · {formatDate(p.created_at)}
                          </span>
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          Pass rate: {Math.round(p.readiness_snapshot.pass_rate * 100)}% ·
                          Unauthorized senders: {p.readiness_snapshot.unauthorized_senders}
                          {p.readiness_snapshot.quarantine_false_positive_rate !== undefined && (
                            <> · FP rate: {(p.readiness_snapshot.quarantine_false_positive_rate * 100).toFixed(2)}%</>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
